import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { LineChart, CheckCircle, Users, TrendingUp, Database, Lightbulb } from 'lucide-react';

const DataScientists = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What should data scientists include in their performance review?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Include model performance metrics (accuracy, precision, recall), business impact of deployed models, experiments run, insights delivered to stakeholders, and infrastructure improvements. Quantify impact: 'Deployed churn prediction model achieving 89% recall, preventing $2.1M in annual revenue loss.'"
        }
      },
      {
        "@type": "Question",
        "name": "How do data scientists demonstrate business impact?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Connect technical work to business outcomes: revenue influenced, costs saved, efficiency gains, and decisions informed by your analysis. Translate model improvements into business terms: 'Improved recommendation accuracy by 15%, driving 8% increase in average order value.'"
        }
      },
      {
        "@type": "Question",
        "name": "What metrics matter for data science performance reviews?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Key metrics include: model performance (accuracy, AUC, F1), business KPIs influenced, experiments completed, models deployed to production, and analysis turnaround time. Also track stakeholder satisfaction, code quality contributions, and knowledge sharing."
        }
      },
      {
        "@type": "Question",
        "name": "How often should data scientists document their achievements?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Document after each model deployment, significant analysis delivery, or stakeholder presentation. Track experiment results, insights delivered, and feedback received as they happen. Use a tool like BragBox to capture wins in real-time."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Performance Review Tips for Data Scientists | BragBox</title>
        <meta name="description" content="Ace your data science performance review with actionable tips. Learn how to showcase model impact, translate technical wins to business value, and document your data-driven achievements." />
        <meta name="keywords" content="data scientist performance review, ML engineer self-evaluation, data science metrics, machine learning career growth, analytics review tips" />
        <link rel="canonical" href="https://bragbox.ai/tips/data-scientists" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#1F2A44] via-[#2d3a5c] to-[#1F2A44]">
        {/* Header */}
        <header className="bg-[#1F2A44]/90 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                <span className="text-[#1F2A44] font-bold text-sm">B</span>
              </div>
              <span className="text-white font-semibold text-lg">BragBox</span>
            </Link>
            <Link
              to="/login?signup=true"
              className="bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Start free
            </Link>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#C4A35A]/20 text-[#C4A35A] px-4 py-2 rounded-full text-sm mb-6">
              <LineChart className="w-4 h-4" />
              Data Science Career Tips
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Performance Review Tips for Data Scientists
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Your models and analyses drive decisions, but technical excellence doesn't always translate to obvious business value. Learn how to document your impact in terms stakeholders understand.
            </p>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-12 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Why Data Scientists Struggle with Self-Evaluations</h2>
            <p className="text-gray-300 mb-6">
              You improved model accuracy by 12 points. You built a feature store that accelerated ML development across the team. You discovered the insight that changed the marketing strategy. But when review time comes, leadership asks "What's the ROI?" and you struggle to translate technical wins into business language.
            </p>
            <p className="text-gray-300">
              Data science impact is often invisible because your best work enables others to succeed. The model that powers the recommendation engine? Product owns the feature. The analysis that informed the pricing decision? Finance takes credit for revenue growth. Your contributions are embedded in others' outcomes.
            </p>
          </div>
        </section>

        {/* Tips Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">10 Actionable Tips for Your Data Science Review</h2>
            
            <div className="space-y-8">
              {/* Tip 1 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">1. Quantify Model Business Impact</h3>
                    <p className="text-gray-300 mb-3">
                      Don't just report model metrics. Translate accuracy improvements into revenue, cost savings, or efficiency gains.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Deployed churn prediction model achieving 87% recall (up from 71%). Model identified 340 at-risk customers, enabling retention campaigns that saved $890K in annual recurring revenue. Precision improvements reduced false positive outreach cost by $45K."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 2 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">2. Document Analysis That Drove Decisions</h3>
                    <p className="text-gray-300 mb-3">
                      Insights are valuable when they inform action. Track analyses you delivered and the decisions or strategies they influenced.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Delivered customer segmentation analysis that identified $1.2M opportunity in underserved segment. Analysis informed new product tier launch. Pricing elasticity study led to 8% price increase with minimal churn impact, adding $340K annual revenue."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 3 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Database className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">3. Track Infrastructure and Platform Contributions</h3>
                    <p className="text-gray-300 mb-3">
                      Pipelines, feature stores, and ML platforms enable team velocity. Document infrastructure you've built and its impact on team productivity.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Built feature store serving 45 features across 6 models, reducing feature engineering time by 60%. Automated ML pipeline reduced model deployment time from 2 weeks to 2 days. Infrastructure improvements enabled 3x increase in experiments run quarterly."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 4 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">4. Document Experiments Run and Learnings</h3>
                    <p className="text-gray-300 mb-3">
                      Negative results are still results. Track experiments conducted, hypotheses tested, and organizational learnings from both successes and failures.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Ran 23 A/B tests supporting product decisions. 8 experiments drove shipping decisions, 4 prevented shipping low-impact features (saving ~6 engineering months). Established experimentation framework now used by 3 product teams."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 5 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">5. Highlight Stakeholder Enablement</h3>
                    <p className="text-gray-300 mb-3">
                      Dashboards, self-service tools, and training empower stakeholders to use data. Track tools you built that democratized data access.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Built executive dashboard used daily by 12 stakeholders, reducing ad-hoc analysis requests by 40%. Created self-service segmentation tool enabling Marketing to run queries without DS support. Trained 15 analysts on SQL, improving their independence."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 6 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">6</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">6. Track Model Deployment and Production Impact</h3>
                    <p className="text-gray-300 mb-3">
                      Models in production are more valuable than notebooks. Document models you deployed and their ongoing business impact.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Deployed 4 models to production serving 50K+ daily predictions. Recommendation model increased click-through rate 23%, contributing to $1.4M incremental revenue. Fraud model blocked $670K in fraudulent transactions with 0.1% false positive rate."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 7 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">7</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">7. Document Data Quality Improvements</h3>
                    <p className="text-gray-300 mb-3">
                      Clean data enables better analysis. Track data quality issues you identified and fixed, and their impact on downstream work.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Identified and fixed data pipeline bug causing 15% of transactions to be miscategorized. Implemented data validation checks preventing 3 potential analysis errors. Created data quality dashboard reducing data issue investigation time by 70%."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 8 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">8</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">8. Highlight Cross-Functional Collaboration</h3>
                    <p className="text-gray-300 mb-3">
                      Data science is inherently collaborative. Document partnerships with product, engineering, and business teams.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Partnered with Product on 5 feature launches, providing data requirements and success metrics. Collaborated with Engineering on ML infrastructure, improving model serving latency by 40%. Led data requirements for Finance's new reporting system."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 9 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">9</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">9. Track Knowledge Sharing and Mentorship</h3>
                    <p className="text-gray-300 mb-3">
                      Growing team capabilities shows leadership. Document training delivered, documentation created, and colleagues mentored.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Delivered 6 internal tech talks on ML best practices with 80+ total attendees. Mentored 2 junior data scientists through promotion. Created documentation for 5 critical data pipelines, reducing onboarding time by 50%."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 10 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">10</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">10. Document Wins Continuously</h3>
                    <p className="text-gray-300 mb-3">
                      Don't wait until review season to remember your impact. Log model deployments, analysis deliveries, and stakeholder feedback in real-time.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Pro Tip:</p>
                      <p className="text-gray-300 text-sm">After every model deployment, stakeholder presentation, or positive feedback, take 30 seconds to log it in BragBox. Include both technical metrics (AUC, accuracy) AND business impact (revenue, cost savings). Your review will practically write itself.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Metrics Examples Section */}
        <section className="py-16 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Key Metrics Data Scientists Should Track</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Model Performance</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Accuracy, precision, recall, F1</li>
                  <li>• AUC-ROC curves</li>
                  <li>• Model drift metrics</li>
                  <li>• Inference latency</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Business Impact</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Revenue influenced</li>
                  <li>• Cost savings from models</li>
                  <li>• Decisions informed by analysis</li>
                  <li>• Efficiency gains enabled</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Productivity</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Models deployed to production</li>
                  <li>• Experiments completed</li>
                  <li>• Analysis turnaround time</li>
                  <li>• Pipeline reliability</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Team Contribution</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Infrastructure improvements</li>
                  <li>• Documentation created</li>
                  <li>• Knowledge sharing delivered</li>
                  <li>• Stakeholder satisfaction</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">What should data scientists include in their performance review?</h3>
                <p className="text-gray-300">Include model performance metrics (accuracy, precision, recall), business impact of deployed models, experiments run, insights delivered to stakeholders, and infrastructure improvements. Quantify impact: "Deployed churn prediction model achieving 89% recall, preventing $2.1M in annual revenue loss."</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How do data scientists demonstrate business impact?</h3>
                <p className="text-gray-300">Connect technical work to business outcomes: revenue influenced, costs saved, efficiency gains, and decisions informed by your analysis. Translate model improvements into business terms: "Improved recommendation accuracy by 15%, driving 8% increase in average order value."</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">What metrics matter for data science performance reviews?</h3>
                <p className="text-gray-300">Key metrics include: model performance (accuracy, AUC, F1), business KPIs influenced, experiments completed, models deployed to production, and analysis turnaround time. Also track stakeholder satisfaction, code quality contributions, and knowledge sharing.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How often should data scientists document their achievements?</h3>
                <p className="text-gray-300">Document after each model deployment, significant analysis delivery, or stakeholder presentation. Track experiment results, insights delivered, and feedback received as they happen. Use a tool like BragBox to capture wins in real-time.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-[#C4A35A]/20 to-[#C4A35A]/10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Your Data Insights Deserve Recognition</h2>
            <p className="text-xl text-gray-300 mb-8">
              BragBox helps data scientists capture model wins, analysis impact, and stakeholder feedback throughout the year.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/login?signup=true"
                className="inline-block bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              >
                Start free
              </Link>
              <Link to="/login" className="text-gray-300 underline underline-offset-4 hover:text-white transition-colors">
                Already have an account? Log in.
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-4 border-t border-white/10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                  <span className="text-[#1F2A44] font-bold text-sm">B</span>
                </div>
                <span className="text-white font-semibold">BragBox</span>
              </Link>
              <div className="flex gap-6 text-gray-400 text-sm">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
                <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
                <Link to="/login" className="hover:text-white transition-colors">Login</Link>
              </div>
              <p className="text-gray-500 text-sm">© 2026 BragBox. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default DataScientists;
